// ===================================================================
// WORKFLOWS — Active Instances
// Shows all currently running workflow instances with progress
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Select, Progress,
  TextInput, Center, Loader,
} from '../../components/gab/GabComponents';
import {
  IconCircleCheck, IconAlertTriangle, IconSearch,
} from '@tabler/icons-react';
import { useWorkflowStore } from '../../store/workflowStore';
import type { WorkflowStepType, WorkflowPriority } from '../../types/workflow';
import {
  INSTANCE_STATUS_COLORS, PRIORITY_COLORS,
  STEP_TYPE_COLORS, STEP_TYPE_LABELS,
} from '../../types/workflow';
import { fmtDateTime as fmtDate } from '../../lib/dateFormat';

// ── Helpers ─────────────────────────────────────────────────────

function progressPct(cur: string, total: string): number {
  const c = Number(cur) || 0;
  const t = Number(total) || 1;
  return Math.round((c / t) * 100);
}

// ── Component ───────────────────────────────────────────────────

export default function WorkflowActive() {
  const navigate = useNavigate();
  const { templates, instances, steps, loading, loadAll } = useWorkflowStore();

  const [search, setSearch]                 = useState('');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [moduleFilter, setModuleFilter]     = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => { loadAll(); }, []);

  // ── Filter options ────────────────────────────────────────────

  const templateOptions = useMemo(() => [
    { value: 'all', label: 'All Templates' },
    ...templates.filter(t => t.Rid).map(t => ({ value: String(t.Rid), label: t.name || 'Unnamed' })),
  ], [templates]);

  const moduleOptions = useMemo(() => {
    const mods = [...new Set(instances.map(i => i.sourceModule).filter(Boolean))];
    return [
      { value: 'all', label: 'All Modules' },
      ...mods.map(m => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) })),
    ];
  }, [instances]);

  const priorityOptions = [
    { value: 'all',      label: 'All Priorities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high',     label: 'High' },
    { value: 'normal',   label: 'Normal' },
    { value: 'low',      label: 'Low' },
  ];

  // ── Filtered active instances ─────────────────────────────────

  const activeInstances = useMemo(() => {
    const q = search.toLowerCase();
    return instances
      .filter(i => i.status === 'active' || i.status === 'escalated')
      .filter(i => templateFilter === 'all' || String(i.templateId) === templateFilter)
      .filter(i => moduleFilter   === 'all' || i.sourceModule === moduleFilter)
      .filter(i => priorityFilter === 'all' || i.priority === priorityFilter)
      .filter(i => !q || (i.templateName || '').toLowerCase().includes(q)
        || (i.sourceRecordLabel || '').toLowerCase().includes(q)
        || (i.startedBy || '').toLowerCase().includes(q))
      .sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  }, [instances, search, templateFilter, moduleFilter, priorityFilter]);

  function getCurrentStep(inst: typeof instances[number]) {
    return steps.find(
      s => s.instanceId === String(inst.Rid) && s.stepIndex === String(inst.currentStepIndex ?? '')
    );
  }

  // ── Render ────────────────────────────────────────────────────

  if (loading && instances.length === 0) {
    return <Center py="xl"><Loader /></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Active Workflows</Title>
          <Badge variant="light" color="blue" size="sm">{activeInstances.length}</Badge>
        </Group>
      </Group>

      {/* Search + Filters */}
      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search workflows..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          w={220}
        />
        <Select
          data={templateOptions}
          value={templateFilter}
          onChange={v => setTemplateFilter(v ?? 'all')}
          size="xs"
          w={220}
          placeholder="All Templates"
        />
        <Select
          data={moduleOptions}
          value={moduleFilter}
          onChange={v => setModuleFilter(v ?? 'all')}
          size="xs"
          w={160}
          placeholder="All Modules"
        />
        <Select
          data={priorityOptions}
          value={priorityFilter}
          onChange={v => setPriorityFilter(v ?? 'all')}
          size="xs"
          w={150}
          placeholder="All Priorities"
        />
      </Group>

      {/* Card list */}
      {activeInstances.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Stack align="center" gap="xs">
            <IconCircleCheck size={32} style={{ color: '#059669' }} />
            <Text c="dimmed" ta="center">No active workflows.</Text>
            <Text size="xs" c="dimmed" ta="center">
              Workflows will appear here when triggered or started manually.
            </Text>
          </Stack>
        </Paper>
      ) : (
        <Stack gap="xs">
          {activeInstances.map(inst => {
            const pct         = progressPct(String(inst.currentStepIndex ?? 0), String(inst.totalSteps));
            const currentStep = getCurrentStep(inst);
            const curIdx      = Number(inst.currentStepIndex) || 0;
            const totalSteps  = Number(inst.totalSteps) || 0;
            const priority    = inst.priority as WorkflowPriority;
            const isEscalated = inst.status === 'escalated';

            return (
              <Paper
                key={inst.Rid}
                p="md"
                radius="sm"
                withBorder
                style={{
                  cursor: 'pointer',
                  borderLeft: `3px solid ${isEscalated
                    ? INSTANCE_STATUS_COLORS.escalated
                    : PRIORITY_COLORS[priority] ?? '#ccc'}`,
                  transition: 'box-shadow 0.15s',
                }}
                onClick={() => navigate(`/workflows/instance/${inst.Rid}`)}
              >
                <Group justify="space-between" mb="xs" wrap="nowrap">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Group gap="xs" mb={2} wrap="nowrap">
                      <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {inst.templateName || '—'}
                      </Text>
                      {isEscalated && (
                        <IconAlertTriangle size={14} style={{ color: INSTANCE_STATUS_COLORS.escalated, flexShrink: 0 }} />
                      )}
                    </Group>
                    {inst.sourceRecordLabel && (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {inst.sourceRecordLabel}
                        {inst.sourceModule ? ` · ${inst.sourceModule}` : ''}
                      </Text>
                    )}
                  </div>
                  <Badge
                    size="xs"
                    variant="light"
                    style={{ backgroundColor: PRIORITY_COLORS[priority] + '22', color: PRIORITY_COLORS[priority], flexShrink: 0 }}
                    tt="capitalize"
                  >
                    {inst.priority || 'normal'}
                  </Badge>
                </Group>

                {/* Progress */}
                <Group gap="xs" mb={6} align="center">
                  <Progress
                    value={pct}
                    size="sm"
                    radius="xl"
                    style={{ flex: 1 }}
                    color={pct === 100 ? 'green' : INSTANCE_STATUS_COLORS.active}
                  />
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    Step {curIdx} / {totalSteps}
                  </Text>
                </Group>

                {/* Current step + assignee */}
                {currentStep && (
                  <Group gap="xs" mb={6}>
                    <Text size="xs" c="dimmed">Current:</Text>
                    <Text size="xs" fw={500}>{currentStep.stepName}</Text>
                    <Badge
                      size="xs"
                      variant="dot"
                      style={{ color: STEP_TYPE_COLORS[currentStep.stepType as WorkflowStepType] ?? '#888' }}
                    >
                      {STEP_TYPE_LABELS[currentStep.stepType as WorkflowStepType] ?? currentStep.stepType}
                    </Badge>
                    {currentStep.assignee && (
                      <Text size="xs" c="dimmed">
                        — {currentStep.assigneeName || currentStep.assignee}
                      </Text>
                    )}
                  </Group>
                )}

                {/* Footer */}
                <Text size="xs" c="dimmed">
                  {inst.startedBy ? `Started by ${inst.startedBy}` : 'Started'}
                  {inst.startedAt ? ` · ${fmtDate(inst.startedAt)}` : ''}
                </Text>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
}
